// client/src/hooks/useCommand.js
// Custom hooks for the Command batch workflow module.

import { useState, useEffect, useCallback, useRef } from "react";
import * as api from "../services/api";
import { API_BASE_URL } from "../config/apiBase";

// ─── useConfig ────────────────────────────────────────────────────────────────
export function useConfig() {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api.fetchCommandConfig();
            setConfig(data);
            setError(null);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const save = useCallback(async (data) => {
        setSaving(true);
        try {
            await api.saveCommandConfig(data);
            await load();
            setError(null);
            return true;
        } catch (e) {
            setError(e.message);
            return false;
        } finally {
            setSaving(false);
        }
    }, [load]);

    useEffect(() => { load(); }, [load]);

    return { config, loading, saving, error, save, reload: load };
}

// ─── useLocations ─────────────────────────────────────────────────────────────
export function useLocations() {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetch = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api.fetchCommandLocations();
            setLocations(data);
        } catch (_) { }
        setLoading(false);
    }, []);

    return { locations, loading, fetchLocations: fetch };
}

// ---- useShopifyScopes -------------------------------------------------------
export function useShopifyScopes() {
    const [scopes, setScopes] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api.checkShopifyScopes();
            setScopes(data);
            setError(null);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    return { scopes, loading, error, refresh: load };
}

// ─── useCommandStats ──────────────────────────────────────────────────────────
export function useCommandStats(pollInterval = 5000) {
    const [stats, setStats] = useState(null);
    const intervalRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const data = await api.fetchCommandStats();
            setStats(data);
        } catch (_) { }
    }, []);

    useEffect(() => {
        load();
        intervalRef.current = setInterval(load, pollInterval);
        return () => clearInterval(intervalRef.current);
    }, [load, pollInterval]);

    return { stats, reload: load };
}

// ─── useCommandOrders ─────────────────────────────────────────────────────────
export function useCommandOrders() {
    const [orders, setOrders] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState("created_at");
    const [dir, setDir] = useState("desc");

    const toggleSort = useCallback((col) => {
        if (sort === col) {
            setDir(prev => prev === "asc" ? "desc" : "asc");
        } else {
            setSort(col);
            setDir("asc");
        }
        setPage(1);
    }, [sort]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api.fetchCommandOrders({ page, limit: 50, filter, search, sort, dir });
            setOrders(data.orders || []);
            setTotal(data.total || 0);
            setPages(data.pages || 1);
        } catch (_) { }
        setLoading(false);
    }, [page, filter, search, sort, dir]);

    useEffect(() => { load(); }, [load]);

    return { orders, total, page, pages, loading, filter, search, sort, dir, setPage, setFilter, setSearch, toggleSort, reload: load };
}

// ─── useJob ───────────────────────────────────────────────────────────────────
export function useJob(jobId) {
    const [job, setJob] = useState(null);
    const [sseConnected, setSseConnected] = useState(false);
    const eventSourceRef = useRef(null);

    // Poll for job status
    useEffect(() => {
        if (!jobId) return;
        let active = true;
        const poll = async () => {
            try {
                const data = await api.fetchCommandJob(jobId);
                if (active) setJob(data);
            } catch (_) { }
        };
        poll();
        const interval = setInterval(poll, 3000);
        return () => { active = false; clearInterval(interval); };
    }, [jobId]);

    // SSE connection for live progress
    useEffect(() => {
        if (!jobId) return;
        const apiKey = process.env.REACT_APP_API_KEY || "your_secure_api_key_here";
        const sseUrl = `${API_BASE_URL}/command/jobs/${jobId}/stream?api_key=${encodeURIComponent(apiKey)}`;
        const es = new EventSource(sseUrl);
        eventSourceRef.current = es;
        setSseConnected(true);

        es.addEventListener("progress", (e) => {
            try {
                const data = JSON.parse(e.data);
                setJob(prev => prev ? { ...prev, ...data } : prev);
            } catch (_) { }
        });

        es.addEventListener("done", (e) => {
            try {
                const data = JSON.parse(e.data);
                setJob(prev => prev ? { ...prev, ...data, status: data.status } : prev);
            } catch (_) { }
            es.close();
            setSseConnected(false);
        });

        es.onerror = () => {
            setSseConnected(false);
        };

        return () => { es.close(); setSseConnected(false); };
    }, [jobId]);

    return { job, sseConnected };
}

// ─── useJobs ──────────────────────────────────────────────────────────────────
export function useJobs() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const data = await api.fetchCommandJobs();
            setJobs(data);
        } catch (_) { }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    return { jobs, loading, reload: load };
}

// ─── useCommandResults ────────────────────────────────────────────────────────
export function useCommandResults() {
    const [results, setResults] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [filter, setFilter] = useState("all");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api.fetchCommandResults({ page, limit: 100, filter });
            setResults(data.orders || []);
            setTotal(data.total || 0);
        } catch (_) { }
        setLoading(false);
    }, [page, filter]);

    useEffect(() => { load(); }, [load]);

    return { results, total, loading, page, filter, setPage, setFilter, reload: load };
}
